import React, { useState } from 'react';
import walletService from '../services/walletService';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const AddMoneyModal = ({ isOpen, onClose, wallet, onSuccess }) => {
  const { token } = useAuth();
  const [form, setForm] = useState({
    amount: '',
    paymentReference: `ADMIN_TOPUP_${Math.floor(Math.random() * 1_000_000_000)}`,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setForm({
      amount: '',
      paymentReference: `ADMIN_TOPUP_${Math.floor(Math.random() * 1_000_000_000)}`,
    });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!form.amount || parseFloat(form.amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      if (!form.paymentReference.trim()) {
        setError('Payment reference is required');
        return;
      }

      if (!token) {
        setError('Authentication token not found');
        return;
      }

      await walletService.addMoney(
        token,
        wallet.id,
        parseFloat(form.amount),
        form.paymentReference
      );

      onSuccess();
      resetForm();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to add money');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Money to Wallet</DialogTitle>
          <DialogDescription>Wallet ID: {wallet?.id}</DialogDescription>
        </DialogHeader>

        {error ? (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Amount</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="Enter amount to add"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="paymentReference">Payment Reference</Label>
            <Input id="paymentReference" type="text" value={form.paymentReference} readOnly />
            <p className="text-xs text-muted-foreground">
              Auto-generated on open; changes each time you open the modal
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Money'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddMoneyModal;
